import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ChapterHeading, Reveal } from "./Reveal";
import { scrollToId } from "@/lib/api";

const CONCERNS = [
  "Pragmatic Language",
  "Language Acquisition",
  "Sensory Processing",
  "Social Skills",
  "Personal Care Skills",
  "Self-Regulation",
  "Challenging Behaviors",
];

const SERVICES = [
  {
    title: "Assessment",
    text: "A BCBA-led initial assessment to understand your child's strengths, challenges, and learning style — the foundation of an individualized treatment plan.",
    img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_pediatric-aba/artifacts/9gabagji_Screenshot%202026-09-22%20at%207.09.03%E2%80%AFPM.png",
    alt: "Therapist guiding a smiling child through a colorful table activity",
  },
  {
    title: "In-Home Therapy",
    text: "Care delivered in a familiar, safe environment so mastered skills carry over naturally into daily family life.",
    img: "https://images.unsplash.com/photo-1516627145497-ae6968895b74?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Family reading together at home",
  },
  {
    title: "In-School Support",
    text: "Collaboration with educators and school staff to support learning, participation, and success in the classroom.",
    img: "https://images.unsplash.com/photo-1532330393533-443990a51d10?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxjaGlsZCUyMHRoZXJhcGlzdCUyMHBsYXlpbmd8ZW58MHx8fHwxNzg2NzE3NDIxfDA&ixlib=rb-4.1.0&q=85",
    alt: "Child playing with toy cars at a table",
    link: "/school-support",
  },
  {
    title: "In-Clinic Therapy",
    text: "A structured, supportive clinic setting with opportunities to engage and learn alongside peers.",
    img: "https://images.unsplash.com/photo-1501686637-b7aa9c48a882?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxjaGlsZCUyMHNtaWxpbmclMjBwbGF5aW5nJTIwYmxvY2tzfGVufDB8fHx8MTc4NjcxNzQxMXww&ixlib=rb-4.1.0&q=85",
    alt: "Child playing with colorful blocks",
  },
  {
    title: "Parent Education",
    text: "Practical tools and strategies so parents and caregivers feel confident supporting progress at home and beyond.",
    img: "https://images.unsplash.com/photo-1587235587178-e4a6dbe63726?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxwYXJlbnQlMjBjaGlsZCUyMGxhdWdoaW5nfGVufDB8fHx8MTc4NjcxNzQyMXww&ixlib=rb-4.1.0&q=85",
    alt: "Parent and child laughing together",
  },
  {
    title: "Ongoing Consultation",
    text: "Continuous data review and open communication with every professional on your child's team, so progress never stalls.",
    img: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Child painting with colorful hands",
  },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-white/60 border-y border-brand-lightblue/30">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <ChapterHeading
          number="02"
          eyebrow="Our Services"
          title="Care with purpose."
          accent="Skills for life."
          testid="services-heading"
        />
        <Reveal delay={0.2} className="mb-12 md:mb-16">
          <span
            data-testid="services-opening-badge"
            className="inline-flex items-center rounded-full bg-brand-yellow/60 text-brand-ink text-xs font-semibold uppercase tracking-[0.18em] px-5 py-2"
          >
            Opening Soon — Planned Services
          </span>
        </Reveal>
      </div>

      <Reveal>
        <p className="text-base md:text-lg text-brand-muted max-w-2xl mb-10" data-testid="services-daily-note">
          We offer <span className="text-brand-ink font-semibold">daily ABA programs for pediatric clients</span> —
          and our services aren't limited to pediatrics. We also support teens and adults.{" "}
          <Link to="/ages-programs" data-testid="services-ages-link" className="text-brand-coral font-semibold hover:text-[#e05f5f] transition-colors duration-300">
            See the ages & programs we serve
          </Link>
        </p>
      </Reveal>

      <Reveal>
        <p className="text-base md:text-lg text-brand-muted max-w-2xl mb-6" data-testid="services-concerns-label">
          Our ABA therapy services are being designed to address concerns regarding:
        </p>
        <div className="flex flex-wrap gap-3 mb-16" data-testid="services-concerns-chips">
          {CONCERNS.map((c) => (
            <span
              key={c}
              className="rounded-full border border-brand-lightblue/60 bg-white px-5 py-2 text-sm font-medium text-brand-blue shadow-soft transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-brand-pale"
            >
              {c}
            </span>
          ))}
        </div>
      </Reveal>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="services-grid">
        {SERVICES.map((s, i) => (
          <Reveal key={s.title} delay={0.07 * i}>
            <article
              data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group h-full rounded-[2rem] bg-white border border-brand-lightblue/40 shadow-soft overflow-hidden transition-[transform,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-lift"
            >
              <div className="overflow-hidden">
                <img
                  src={s.img}
                  alt={s.alt}
                  loading="lazy"
                  className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-8">
                <h3 className="font-serif text-2xl font-semibold text-brand-blue mb-3">{s.title}</h3>
                <p className="text-sm md:text-base leading-relaxed text-brand-muted">{s.text}</p>
                {s.link && (
                  <Link
                    to={s.link}
                    data-testid="service-link-in-school"
                    className="group/link mt-5 inline-flex items-center gap-2 text-brand-coral font-semibold text-sm transition-colors duration-300 hover:text-[#e05f5f]"
                  >
                    School & Daycare Programs
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                  </Link>
                )}
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-14 text-center">
        <button
          data-testid="services-cta-intake"
          onClick={() => scrollToId("intake")}
          className="group inline-flex items-center gap-2 rounded-full bg-brand-blue text-white font-semibold px-8 py-4 shadow-soft transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:shadow-lift hover:bg-brand-deep"
        >
          Start Your Child's Intake
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </Reveal>
    </div>
  </section>
);
